import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Button, Collapse, Group, Image, Input, Stack, Text, TextInput } from '@mantine/core';
import { Dropzone, MIME_TYPES, type FileRejection, type FileWithPath } from '@mantine/dropzone';
import { useDisclosure } from '@mantine/hooks';
import { IconPhoto, IconUpload, IconX } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toApiError } from '../apollo/errors';
import { AdminCreateUploadDocument } from '../graphql/operations';

/**
 * What the signed policy allows, repeated here so a file the storage would
 * refuse never leaves the browser. The numbers are the API's
 * (`content-length-range 1 5242880`); they are a client-side courtesy, not the
 * rule — the rule is in the signature.
 */
export const ARTWORK_MAX_MB = 5;
const ARTWORK_MAX_BYTES = ARTWORK_MAX_MB * 1024 * 1024;
const ARTWORK_MIME_TYPES: string[] = [
  MIME_TYPES.png,
  MIME_TYPES.jpeg,
  MIME_TYPES.webp,
  MIME_TYPES.gif,
];

export const ARTWORK_TOO_LARGE_KEY = 'catalog.form.artworkErrors.tooLarge';
export const ARTWORK_WRONG_TYPE_KEY = 'catalog.form.artworkErrors.wrongType';
export const ARTWORK_UPLOAD_FAILED_KEY = 'catalog.form.artworkErrors.uploadFailed';
export const ARTWORK_FAILED_KEY = 'catalog.form.artworkErrors.failed';

/** One object in the store that no row points at yet. */
export type ArtworkUpload = { key: string; publicUrl: string };

/**
 * The artwork of one row — a catalogue item, or a cross-promo app: a picture
 * to drop, a preview of what the row will show, and — folded away — the URL
 * itself.
 *
 * The file never passes through the API. `adminCreateUpload` signs a one-shot
 * permission, the browser POSTs the file straight to the object store, and what
 * is saved on the row is the public URL that upload produced. Two consequences
 * shape this component:
 *
 * - the key is built from the row's id, so nothing can be uploaded before the
 *   id is a valid slug — hence `idReady`, which disables the dropzone for a new
 *   row until its identifier is typed;
 * - an upload that the operator then abandons leaves an object behind, so every
 *   successful one is reported through `onUploaded` and the modal deletes what
 *   it did not save.
 */
export function ArtworkField({
  collection,
  itemId,
  idReady,
  value,
  onChange,
  onUploaded,
  onFieldErrors,
}: {
  /** A catalogue table, or another admin-owned upload namespace such as `cross-promo`. */
  collection: string;
  /** The row's id as typed; the key is derived from it. */
  itemId: string;
  /** Whether that id passes the slug rule — until it does, nothing can be sent. */
  idReady: boolean;
  value: string;
  onChange: (artworkUrl: string) => void;
  onUploaded: (upload: ArtworkUpload) => void;
  /** The API's per-field messages, e.g. an `id` it refused. */
  onFieldErrors: (fields: Record<string, string>) => void;
}) {
  const { t } = useTranslation();
  const [createUpload] = useMutation(AdminCreateUploadDocument);
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [advancedOpen, advanced] = useDisclosure(false);
  // Mantine hands back the dropzone's own file picker, which is what the
  // "Replace" button below the preview opens.
  const openPicker = useRef<() => void>(null);

  const preview = value.trim();

  async function upload(file: FileWithPath) {
    setFailure(null);

    // The same two rules the dropzone enforces, for a file that reached this
    // function some other way (a drop into a browser that ignored `accept`).
    if (!ARTWORK_MIME_TYPES.includes(file.type)) {
      setFailure(t(ARTWORK_WRONG_TYPE_KEY));

      return;
    }

    if (file.size > ARTWORK_MAX_BYTES) {
      setFailure(t(ARTWORK_TOO_LARGE_KEY, { limit: ARTWORK_MAX_MB }));

      return;
    }

    setUploading(true);

    try {
      const { data } = await createUpload({
        variables: {
          input: { collection, id: itemId, contentType: file.type, fileName: file.name },
        },
      });
      const signed = data?.adminCreateUpload;

      if (!signed) {
        setFailure(t(ARTWORK_FAILED_KEY));

        return;
      }

      const body = new FormData();

      // Every signed field first and `file` last, without exception: S3 and
      // Garage stop reading the multipart body at the file part, so a field sent
      // after it simply is not there when the policy is checked.
      for (const [name, field] of Object.entries(asFields(signed.fields))) body.append(name, field);

      body.append('file', file);

      // No `Content-Type` header: the boundary is the browser's to write, and
      // setting the header by hand loses it.
      const response = await fetch(signed.url, { method: 'POST', body });

      // A presigned POST answers 204 with an empty body. Anything else is the
      // storage refusing the form — an oversize file, an expired signature — and
      // it answers in XML nobody here should try to read.
      if (response.status !== 204) {
        setFailure(t(ARTWORK_UPLOAD_FAILED_KEY, { status: response.status }));

        return;
      }

      onUploaded({ key: signed.key, publicUrl: signed.publicUrl });
      onChange(signed.publicUrl);
    } catch (thrown) {
      const error = toApiError(thrown as ErrorLike);
      const fields = error ? Object.keys(error.fields) : [];

      // A refused id belongs under the id input, not under the dropzone.
      if (error && fields.length > 0) onFieldErrors(error.fields);

      setFailure(error?.message ?? t(ARTWORK_FAILED_KEY));
    } finally {
      setUploading(false);
    }
  }

  function reject(rejections: FileRejection[]) {
    const code = rejections[0]?.errors[0]?.code;

    setFailure(
      code === 'file-too-large'
        ? t(ARTWORK_TOO_LARGE_KEY, { limit: ARTWORK_MAX_MB })
        : t(ARTWORK_WRONG_TYPE_KEY),
    );
  }

  return (
    <Stack gap="xs">
      <Input.Wrapper
        label={t('catalog.form.artwork')}
        description={t('catalog.form.artworkDescription', { limit: ARTWORK_MAX_MB })}
        error={failure}
      >
        <Dropzone
          mt={6}
          onDrop={(files) => {
            const file = files[0];

            if (file) void upload(file);
          }}
          onReject={reject}
          accept={ARTWORK_MIME_TYPES}
          maxSize={ARTWORK_MAX_BYTES}
          maxFiles={1}
          multiple={false}
          loading={uploading}
          disabled={!idReady}
          openRef={openPicker}
          radius="md"
          // The dropzone is the row's picture as much as its input, so the
          // preview lives inside it and a drop replaces what is shown.
          inputProps={{ 'aria-label': t('catalog.form.artworkPicker') }}
        >
          <Group gap="md" wrap="nowrap" mih={88} align="center" style={{ pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconUpload size={36} stroke={1.5} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={36} stroke={1.5} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              {preview ? (
                <Image src={preview} alt="" h={72} w={72} radius="md" fit="contain" />
              ) : (
                <IconPhoto size={36} stroke={1.5} />
              )}
            </Dropzone.Idle>

            <Text size="sm" c={idReady ? undefined : 'dimmed'}>
              {idReady ? t('catalog.form.artworkDropzone') : t('catalog.form.artworkNeedsId')}
            </Text>
          </Group>
        </Dropzone>
      </Input.Wrapper>

      <Group gap="xs">
        <Button
          variant="default"
          size="xs"
          disabled={!idReady || uploading}
          onClick={() => openPicker.current?.()}
        >
          {preview ? t('catalog.form.artworkReplace') : t('catalog.form.artworkPick')}
        </Button>

        {preview && (
          <Button
            variant="subtle"
            color="red"
            size="xs"
            disabled={uploading}
            onClick={() => {
              setFailure(null);
              onChange('');
            }}
          >
            {t('catalog.form.artworkRemove')}
          </Button>
        )}

        <Button
          variant="subtle"
          size="xs"
          ml="auto"
          onClick={advanced.toggle}
          aria-expanded={advancedOpen}
        >
          {t('catalog.form.artworkAdvanced')}
        </Button>
      </Group>

      {/* A row may point at any host, so pasting a URL stays possible — just
          not in the way of the operator who only wants to drop a file. */}
      <Collapse in={advancedOpen}>
        <TextInput
          label={t('catalog.form.artworkUrl')}
          description={t('catalog.form.artworkUrlDescription')}
          placeholder={t('catalog.form.artworkPlaceholder')}
          value={value}
          onChange={(event) => {
            onChange(event.currentTarget.value);
          }}
        />
      </Collapse>
    </Stack>
  );
}

/**
 * `fields` is a `JSON` scalar — the storage decides what is in it, and this
 * console only has to hand it back unread. Values are stringified because that
 * is what `FormData` does anyway, and doing it here keeps a stray number out of
 * the `append` overloads.
 */
function asFields(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object') return {};

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([name, field]) => [name, String(field)]),
  );
}
